import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ProgressBarModule } from 'primeng/progressbar';
import { NgSelectModule } from '@ng-select/ng-select';
import { forkJoin, Observable, switchMap, lastValueFrom } from 'rxjs';
import { insoleComponentesConfig } from 'src/app/components/insole-component/components-config';
import { InsolesPrescriptionComponent } from 'src/app/components/insoles-prescription/insoles-prescription.component';
import { MaterialModule } from 'src/app/material.module';
import { CustomerFormService } from 'src/app/services/customer-form.service';
import { FormResponseService } from 'src/app/services/form-response.service';
import { ProductsService } from 'src/app/services/products.service';
import { ScanService } from 'src/app/services/scan.service';
import { UserService } from 'src/app/services/user.service';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import { StripeService } from 'src/app/services/stripe.service';
import JSZip from 'jszip';
import { User } from 'src/app/models/user.model';
import { Roles } from 'src/app/layouts/full/shared/Enums';
import { ClientsService } from 'src/app/services/clients.service';
import { PrescriptionTemplatesService } from 'src/app/services/prescription-templates.service';

interface JsonFormControls {
  subtitle?: string;
  title?: string;
  value?: string | boolean;
  type?: string;
  options?: any;
  choices?: any
  components?: any;
  products?: any;
  validators?: boolean;
  required?: boolean
  isMultipleFormArry?: boolean;
  formControl?: JsonFormControls[]
  image?: string;
  logic?: Array<{
    condition: string;
    selectedOption: string;
    goToIndex: number;
  }>;
}

export interface JsonFormData {
  controls: JsonFormControls[];
}

@Component({
  selector: 'app-view-custom-form',
  standalone: true,
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, FormsModule, TablerIconsModule, TranslateModule, DialogModule, ButtonModule, TableModule, InsolesPrescriptionComponent, ProgressBarModule, NgSelectModule],
  providers: [MessageService],
  templateUrl: './view-custom-form.component.html',
  styleUrl: './view-custom-form.component.scss'
})
export class ViewCustomFormComponent {

  fomrJson: JsonFormControls[]
  currentStep = 0;
  navigationHistory: number[] = [0];
  singleSelectedObj: any
  tableData = []
  isTableData: boolean = false
  formResponse: any
  payload: any
  public myForm: FormGroup;
  savedFormData: any[] = []; // Guardar las respuestas antes del reset
  productsList: any[] = [];
  priceList = [];
  quantityList = [];
  isCheckoutModal: boolean = false;
  totalAmount: number = 0;
  checkoutPayload: any
  loading: boolean = false;
  formId: string;
  private mouseDownState: boolean = false;
  isSubmitting: boolean = false;
  hasInsolePrescriptionBeenAdded: boolean = false;
  referenceId: string
  errorMessage: string
  clientId: string
  email: string = ""
  emailForm: FormGroup
  userUploadedImages: { [key: string]: { preview: string, file: File, uploaded: boolean, s3Url: string | null } } = {}
  primaryColor: string = '#5d87ff';
  secondaryColor: string = '#49beff';
  user: User
  userLogo: string | null
  is3DPreviewOpen: boolean = false;
  prescriptionTemplateId: string | null = null;
  name: string = '';
  surname: string = '';
  gender: string = '';

  // Mapeo de colores por tema
  private themeColors: { [key: string]: { primary: string, secondary: string } } = {
    'blue_theme': { primary: '#5d87ff', secondary: '#87b3ff' },
    'orange_theme': { primary: '#fa896b', secondary: '#ffb38a' },
    'purple_theme': { primary: '#763ebd', secondary: '#b197e6' },
    'green_theme': { primary: '#0a7ea4', secondary: '#4db6a4' },
    'aqua_theme': { primary: '#0074ba', secondary: '#4fc1e9' },
    'innosolutions_theme': { primary: '#82c11f', secondary: '#b6d85a' }
  };

  @Input() formResponseId: string
  @Input() orderId: string;
  @Input() customFormFromInput: any
  @Input() clientID: string
  @Input() showLogo: boolean = true
  @Output() customFormSubmitted = new EventEmitter<{ formResponseId: any, scanId: any }>()


  constructor(
    private _fb: FormBuilder,
    private route: ActivatedRoute,
    private customerFormService: CustomerFormService,
    private _router: Router,
    private _formResponseService: FormResponseService,
    private messageService: MessageService,
    private productsService: ProductsService,
    private stripeService: StripeService,
    private scanService: ScanService,
    private userService: UserService,
    private clientService: ClientsService,
    private prescriptionTemplatesService: PrescriptionTemplatesService
  ) {
    this.user = this.userService.user ? this.userService.user : null

    this.myForm = this._fb.group({
      customForm: new FormArray([])
    });

    this.emailForm = this._fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    // Obtener colores del tema del usuario
    const activeTheme = this.user?.appearance?.activeTheme || 'blue_theme';

    // Primero intentar usar los colores del tema activo
    if (this.themeColors[activeTheme]) {
      this.primaryColor = this.themeColors[activeTheme].primary;
      this.secondaryColor = this.themeColors[activeTheme].secondary;
    }

    if (this.user?.appearance?.logo) {
      this.userLogo = this.user.appearance.logo;
    }

    if (this.customFormFromInput) {
      this.myForm = this._fb.group({
        customForm: new FormArray([])
      });

      this.formId = this.customFormFromInput.ID
      this.formResponse = this.customFormFromInput
      this.fomrJson = this.customFormFromInput.questions
      this.currentStep = 0;
      this.navigationHistory = [0]; // Reiniciar historial
      this.createForm(this.fomrJson)

      // Si le ha llegado el clientID por input es que lo necesita para buscar prescription templates
      if (this.clientID) {
        this.clientId = this.clientID
      }

      const userId = this.customFormFromInput.userId
      this.applyCustomFormAppearance(userId, this.customFormFromInput.appearance)
    } else {
      this.route.params.subscribe(params => {
        this.formId = params['id']
        this.referenceId = params['referenceId']
        this.customerFormService.getFormById(this.formId).subscribe((res: any) => {
          this.formResponse = res;
          if (this.formResponse.clientId) {
            this.clientId = this.formResponse.clientId
          }

          const userId = res.userId
          this.applyCustomFormAppearance(userId, this.formResponse.appearance)

          this.fomrJson = res.questions;
          this.navigationHistory = [0]; // Reiniciar historial
          this.createForm(this.fomrJson);
        })
      });

      this.route.queryParams.subscribe(params => {
        const clientIdParam = params['clientId'];

        if (clientIdParam) {
          this.clientId = clientIdParam;
        } else {
          console.log("No viene clientId en los queryParams");
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.ngOnInit()
  }

  onThreeDPreviewVisibleChange(isOpen: boolean): void {
    this.is3DPreviewOpen = isOpen;
  }

  async applyCustomFormAppearance(userId: string, appearance?: { activeTheme: string, logo: string }) {
    try {
      const user: User = await lastValueFrom(this.userService.getUserById(userId));

      this.user = user;

      if (this.user?.role === Roles.ROLE_DISTRIBUTOR) {
        const theme = this.user.appearance?.activeTheme;
        if (theme && this.themeColors[theme]) {
          this.primaryColor = this.themeColors[theme].primary;
          this.secondaryColor = this.themeColors[theme].secondary;
        }
      }

      if (this.user?.role === Roles.ROLE_CUSTOMER) {
        const clientRes: any = await lastValueFrom(this.clientService.getClient(this.user.email));
        const distributorId = clientRes.userId;

        const distUser: any = await lastValueFrom(this.userService.getUserById(distributorId));

        const theme = distUser.appearance?.activeTheme;
        if (theme && this.themeColors[theme]) {
          this.primaryColor = this.themeColors[theme].primary;
          this.secondaryColor = this.themeColors[theme].secondary;
        }
      }

      if (this.user.appearance?.logo) {
        this.userLogo = this.user.appearance.logo;
      }

    } catch (error) {
      // Si no hay user loggeado entra en el catch

      if (appearance && appearance.activeTheme && this.themeColors[appearance.activeTheme]) {
        this.primaryColor = this.themeColors[appearance.activeTheme].primary;
        this.secondaryColor = this.themeColors[appearance.activeTheme].secondary;
      }

      if (appearance?.logo) {
        this.userLogo = appearance.logo;
      }
    }
  }

  createForm(controls: JsonFormControls[]) {
    const customArray = this.myForm.controls['customForm'] as FormArray;
    controls.forEach(async (control, index) => {
      let form: FormGroup = this._fb.group({});

      const formControl = control.type! + index
      if (control.required) {
        if (control?.components || control?.components?.length > 0) {
          const components: any = new FormArray([]);
          control.components.forEach((ele: any, i: any) => {
            const componentForm = this._fb.group({})
            const numberInputWithLabels: any = new FormArray([]);
            ele.choices.numberInputWithLabels.forEach((element: any, ind: any) => {
              numberInputWithLabels.push(this._fb.control('', Validators.required));
            })
            componentForm.addControl('numberInputWithLabels', numberInputWithLabels);
            const checkboxWithLabel: any = new FormArray([]);
            ele.choices.checkboxWithLabel.forEach((element: any, ind: any) => {
              // const formControl = 'checkbox' + index + i + ind
              const formArray: any = new FormArray([], Validators.required);
              element.options.forEach((option: any) => {
                const control = this._fb.control(false);
                formArray.push(control);
              });
              checkboxWithLabel.push(formArray);
            })
            componentForm.addControl('checkboxWithLabel', checkboxWithLabel);
            const dropdownWithLabel: any = new FormArray([]);
            ele.choices.dropdownWithLabel.forEach((element: any, ind: any) => {
              dropdownWithLabel.push(this._fb.control('', Validators.required))
            })
            componentForm.addControl('dropdownWithLabel', dropdownWithLabel);
            const radioButtons: any = new FormArray([]);
            ele.choices.radioButtons.forEach((element: any, ind: any) => {
              radioButtons.push(this._fb.control('', Validators.required))
            })
            componentForm.addControl('radioButtons', radioButtons);
            components.push(componentForm);
          })
          form.addControl('components', components);
          form.updateValueAndValidity();
        } else {
          if (control.type === 'email') {
            form.addControl('email', this._fb.control('', [Validators.required, Validators.email, ViewCustomFormComponent.noSpaceValidator()]));
          } else if (control.type === 'requiredImages') {
            // Para requiredImages requeridas, validar que se hayan subido todas las imágenes
            form.addControl('requiredImages', this._fb.control([], [Validators.required, this.requiredImagesValidator(control.choices?.length || 0)]));
          } else {
            form.addControl(control.type!, this._fb.control('', [Validators.required, ViewCustomFormComponent.noSpaceValidator()]));
          }
        }
      } else {
        if (control?.components || control?.components?.length > 0) {
          const components: any = new FormArray([]);

          control.components.forEach((ele: any, i: any) => {
            const componentForm = this._fb.group({})
            const numberInputWithLabels: any = new FormArray([]);
            ele.choices.numberInputWithLabels.forEach((element: any, ind: any) => {
              numberInputWithLabels.push(this._fb.control(''));
            })
            componentForm.addControl('numberInputWithLabels', numberInputWithLabels);
            const checkboxWithLabel: any = new FormArray([]);
            ele.choices.checkboxWithLabel.forEach((element: any, ind: any) => {
              const formArray: any = new FormArray([]);
              element.options.forEach((option: any) => {
                const control = this._fb.control(false);
                formArray.push(control);
              });
              checkboxWithLabel.push(formArray);
            })
            componentForm.addControl('checkboxWithLabel', checkboxWithLabel);
            const dropdownWithLabel: any = new FormArray([]);
            ele.choices.dropdownWithLabel.forEach((element: any, ind: any) => {
              dropdownWithLabel.push(this._fb.control(''))
            })
            componentForm.addControl('dropdownWithLabel', dropdownWithLabel);
            const radioButtons: any = new FormArray([]);
            ele.choices.radioButtons.forEach((element: any, ind: any) => {
              radioButtons.push(this._fb.control(''))
            })
            componentForm.addControl('radioButtons', radioButtons);
            components.push(componentForm);
          })
          form.addControl('components', components);
          form.updateValueAndValidity();
        } else {
          if (control.type === "insolesPrescription") {

            let insoleComponentsConfig = insoleComponentesConfig;
            let insoleComponentsForms: any = [];

            // Add all the components to the components prescription form
            insoleComponentsConfig.map((component) => {
              insoleComponentsForms.push(component.form)
            })

            // Create Insoles Prescription Form
            let insolesPrescriptionFormGroup = this._fb.group({
              type: new FormControl('insolesPrescription', { nonNullable: true }),
              components: this._fb.array(insoleComponentsForms)
            })

            insolesPrescriptionFormGroup.reset();


            form = insolesPrescriptionFormGroup;
          } else if (control.type === 'requiredImages') {
            const validators = control.required ? [Validators.required] : [];
            form.addControl('requiredImages', this._fb.control([], validators));
          } else {
            form.addControl(control.type!, this._fb.control(''));
          }
        }
      }

      customArray.push(form);
      customArray.updateValueAndValidity();
      this.myForm.updateValueAndValidity();
      if (control.type === 'productsList') {
        const productObservables: Observable<any>[] = control.products.map((productId: string) => {
          // Tenemos que pasarle el connectedStripeId del role customer
          const stripeAccount = this.formResponse.connectedStripeId

          // Create an observable for getProductById
          const getProductById$ = this.productsService.getProductById(productId, stripeAccount);

          // Create an observable for getPriceFromProduct (Antes en el servicio no se mandaba stripeAccount, el valor del parámetro era directamente 1)
          const getPriceFromProduct$ = this.productsService.getPriceFromProduct(productId, stripeAccount);

          // Combine the two observables into a single observable using forkJoin
          return forkJoin([getProductById$, getPriceFromProduct$]).pipe(
            switchMap(([productResponse, priceResponse]: [any, any]) => {
              const product = productResponse.product;
              const price = priceResponse.prices[0];
              return [{ product, price, quantity: 0 }];
            })
          );
        });

        // Use forkJoin to execute all observables in parallel
        forkJoin(productObservables).subscribe((productsList: any[]) => {
          this.productsList = productsList;
        });
      }
    })
  }

  get customFormControls() {
    return this.myForm.controls['customForm'] as FormArray;
  }

  customFormArrayControls(i: any) {
    return this.customFormControls.controls[i] as FormGroup;
  }

  numberInputControls(i: any, j: any, k: any) {
    return (((this.customFormArrayControls(i).controls['components'] as FormArray).controls[j] as FormGroup).controls['numberInputWithLabels'] as FormArray).controls[k];
  }

  dropdownControls(i: any, j: any, k: any) {
    return (((this.customFormArrayControls(i).controls['components'] as FormArray).controls[j] as FormGroup).controls['dropdownWithLabel'] as FormArray).controls[k];
  }

  isNumberInputValid(i: any, j: any, k: any) {
    return this.numberInputControls(i, j, k)?.errors && (this.numberInputControls(i, j, k)?.dirty || this.numberInputControls(i, j, k)?.touched)
  }

  isDropdownValid(i: any, j: any, k: any) {
    return this.dropdownControls(i, j, k)?.errors && (this.dropdownControls(i, j, k)?.dirty || this.dropdownControls(i, j, k)?.touched)
  }

  isCustomFormValid(i: any) {
    return this.customFormControls.controls[i].invalid;
  }

  isFormInvalidForStep(i: any): boolean {
    const control = this.customFormControls.controls[i];
    const currentQuestion = this.fomrJson[i];

    // Check if form control is invalid
    if (control.invalid) {
      return true;
    }

    // Special validation for dropdown type
    if (currentQuestion.type === 'dropdown' && control instanceof FormGroup) {
      const dropdownControl = control.get('dropdown');
      return !dropdownControl?.value || dropdownControl.value === '';
    }

    // Check if form is blank (excluding productsList)
    return this.isFormBlank(control);
  }

  isDropdownInvalid(i: any): boolean {
    const control = this.customFormControls.controls[i];
    if (control instanceof FormGroup) {
      const dropdownControl = control.get('dropdown');
      return !!(dropdownControl?.errors && (dropdownControl?.touched || dropdownControl?.dirty));
    }
    return false;
  }

  markDropdownAsTouched(i: any): void {
    const control = this.customFormControls.controls[i];
    if (control instanceof FormGroup) {
      const dropdownControl = control.get('dropdown');
      dropdownControl?.markAsTouched();
    }
  }

  onDropdownChange(i: any): void {
    const control = this.customFormControls.controls[i];
    if (control instanceof FormGroup) {
      const dropdownControl = control.get('dropdown');
      dropdownControl?.markAsDirty();
      dropdownControl?.updateValueAndValidity();
    }
  }

  // Helper function to get patient data from questions using unique IDs
  private getPatientDataFromQuestions(questions: any[]): { name: string, surname: string, gender: string } {

    const nameQuestion = questions.find((question: any) => question.id === 'patient_name');
    const surnameQuestion = questions.find((question: any) => question.id === 'patient_surname');
    const genderQuestion = questions.find((question: any) => question.id === 'patient_gender');

    const result = {
      name: nameQuestion?.response,
      surname: surnameQuestion?.response,
      gender: genderQuestion?.response
    };

    return result;
  }

  async onSubmit() {
    // Verificar si ya se está enviando
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    Swal.fire({
      icon: "info",
      title: "建立秩序"
    });
    Swal.showLoading();

    try {
      const data = this.customFormControls.getRawValue();


      const responseData: any = {}
      const questionData: any = JSON.parse(JSON.stringify(this.formResponse.questions));

      // this.isTableData = true
      data.forEach(item => {
        const keys = Object.keys(item);
        keys.forEach(key => {
          if (!responseData[key]) {
            responseData[key] = [];
          }
          responseData[key].push(item[key]);
        });
      });

      this.formResponseId = this.formResponseId ?? uuidv4()
      this.orderId = this.orderId ?? uuidv4()

      this.payload = {
        ID: this.formResponseId,
        formId: this.formResponse.ID,
        formTitle: this.formResponse.name,
        requireScans: this.formResponse.requireScans ? true : false,
        requireWeb3DScans: this.formResponse.requireWeb3DScans ? true : false,
        questions: null,
        orderId: this.orderId
      }
      const questions: any = [];
      for (let index = 0; index < data.length; index++) {
        const response = data[index];
        const res = response[Object.keys(response)[0]];

        if (questionData[index].type === 'multipleChoice') {
          const tempRes: any = [];
          res.forEach((r: any) => {
            tempRes.push({
              title: r.title || '',
              image: r.image,
              responses: this.getStringFromArray(r.legSide)
            })
          })
          questionData[index].responses = tempRes;
          questionData[index].image = res[0].image;
          delete questionData[index].choices;
          delete questionData[index].required;
          delete questionData[index].subtitle;
          questions.push(questionData[index]);
        } else if (questionData[index].type === 'uniqueChoice') {
          questionData[index].image = res.image;
          const response = questionData[index].choices.filter((choice: any) => choice.isSelected === true);
          questionData[index].response = response[0].options[0].name;

          // Guardar el prescriptionTemplateId si existe en la choice seleccionada
          if (response[0]?.prescriptionTemplateId) {
            questionData[index].prescriptionTemplateId = response[0].prescriptionTemplateId;
            console.log('Saving prescriptionTemplateId:', response[0].prescriptionTemplateId, 'for question:', questionData[index].title);

            try {
              const prescriptionTemplate = await lastValueFrom(
                this.prescriptionTemplatesService.getPrescriptionTemplateByID(response[0].prescriptionTemplateId)
              );

              if (prescriptionTemplate?.prescription?.components) {
                const question = {
                  title: "Prescription",
                  type: "insolesPrescription",
                  components: prescriptionTemplate.prescription.components
                };
                questions.push(question);
              }
            } catch (error) {
              console.error('Error loading prescription template:', error);
            }
          }

          delete questionData[index].choices;
          delete questionData[index].required;
          delete questionData[index].subtitle;
          questions.push(questionData[index]);
        } else if (questionData[index].type === 'customComponents') {

          res.forEach((r: any, i: any) => {
            r.numberInputWithLabels?.forEach((v: any, j: any) => {
              questionData[index].components[i].choices.numberInputWithLabels[j].inputNumberValue = v.inputNumberValue || v;
            })
            r.dropdownWithLabel?.forEach((v: any, j: any) => {
              questionData[index].components[i].choices.dropdownWithLabel[j].response = v.response || v;
              delete questionData[index].components[i].choices.dropdownWithLabel[j].type;
              delete questionData[index].components[i].choices.dropdownWithLabel[j].options;
            })
            r.radioButtons?.forEach((v: any, j: any) => {
              questionData[index].components[i].choices.radioButtons[j].response = v.response || v;
              delete questionData[index].components[i].choices.radioButtons[j].type;
              delete questionData[index].components[i].choices.radioButtons[j].options;
            })

            r.checkboxWithLabel?.forEach((v: any, j: any) => {
              const responses: any = [];
              v.forEach((check: any, k: any) => {
                if (check === true) {
                  responses.push(questionData[index].components[i].choices.checkboxWithLabel[j].options[k]);
                }
              })
              questionData[index].components[i].choices.checkboxWithLabel[j].responses = responses;
              delete questionData[index].components[i].choices.checkboxWithLabel[j].type;
              delete questionData[index].components[i].choices.checkboxWithLabel[j].default;
              delete questionData[index].components[i].choices.checkboxWithLabel[j].options;
            })
            questionData[index].components[i].choices.multipleCheckboxWithLabel = questionData[index].components[i].choices.checkboxWithLabel;
            questionData[index].components[i].choices.checkboxWithLabel = [];
          })
          questions.push(questionData[index])
          delete questionData[index].required;
          delete questionData[index].subtitle;
        } else if (questionData[index].type === 'insolesPrescription') {

          const customFormArray = this.customFormControls;
          const foundInsolePrescription = customFormArray.controls.find((component) => component.get('type') && component.get('type')!.value)

          const componentsArray = foundInsolePrescription!.get('components') as FormArray;

          // Filter only the componentes that the form is valid and has some foot selected
          // Because when nothing it's selected as "foot" it's always valid
          const validFormsWithFootSelection = componentsArray.controls.filter((formGroup: any) => {
            return formGroup.valid && formGroup.get('footSelection')!.value;
          });

          // Prepare array of components to request
          let componentsToRequest = [];

          // Add all the valid components to the components list
          validFormsWithFootSelection.forEach(formGroup => {
            componentsToRequest.push(formGroup.value)
          });

          // Map the data to request, and list only components "valid" and with Foot selected
          const insolePrescriptionForm = {
            ...foundInsolePrescription!.value,
            title: questionData[index].title,
            components: validFormsWithFootSelection.map(component => component.value)
          };

          // Agregar prescriptionTemplateId si fue guardado desde la template
          if (foundInsolePrescription!.get('prescriptionTemplateId')?.value) {
            insolePrescriptionForm.prescriptionTemplateId = foundInsolePrescription!.get('prescriptionTemplateId')!.value;
          }

          questions.push(insolePrescriptionForm);
        } else if (questionData[index].type === 'requiredImages') {
          const question: any = {};
          question.title = questionData[index].title;
          question.type = questionData[index].type;
          // Copy the ID if it exists (important for patient data questions)
          if (questionData[index].id) {
            question.id = questionData[index].id;
          }

          // Inicialmente crear la pregunta sin el ZIP URL
          question.responses = res.map((response: any) => ({
            title: response.title,
            exampleImage: response.exampleImage,
            index: response.index
          }));

          questions.push(question);
        } else if (questionData[index].type === 'dropdown') {
          const question: any = {};
          question.title = questionData[index].title;
          question.type = questionData[index].type;
          // Para dropdown, res contiene directamente el valor seleccionado
          question.response = res || '';
          // Copy the ID if it exists (important for patient data questions)
          if (questionData[index].id) {
            question.id = questionData[index].id;
          }
          questions.push(question);
        } else {
          const question: any = {};
          question.title = questionData[index].title;
          question.type = questionData[index].type;
          question.response = res || '';
          // Copy the ID if it exists (important for patient data questions)
          if (questionData[index].id) {
            question.id = questionData[index].id;
          }
          questions.push(question);
        }

      }
      this.payload.questions = questions;

      Swal.update({
        title: "Submitting form..."
      });

      this._formResponseService.submitFormResponse(this.payload).subscribe(async (res) => {

        // DO NOT REMOVE THIS CONSOLE.LOG
        // It's used to communicate on iOS App
        // DO NOT REMOVE THIS CONSOLE.LOG
        console.log(JSON.stringify({ formResponseId: this.formResponseId, scanId: this.orderId }));

        // Send message to parent window (for iframe integration)
        if (window.parent) {
          window.parent.postMessage({
            event: 'customFormSubmitted',
            formResponseId: this.formResponseId,
            scanId: this.orderId
          }, '*');
        }

        try {
          Swal.close()
          this.isSubmitting = false;

          const requireScans = this.formResponse.requireScans;
          const requireWeb3DScans = this.formResponse.requireWeb3DScans
          const hasProductsList = this.formResponse.questions.some((question: any) => question.type === 'productsList');

          if (this.customFormFromInput) {
            // Guardar las respuestas ANTES de resetear el formulario
            const hasRequiredImages = Object.keys(this.userUploadedImages).length > 0;
            if (hasRequiredImages) {
              this.savedFormData = this.customFormControls.getRawValue();
            }

            // Emitir el evento PRIMERO
            this.customFormSubmitted.emit({
              formResponseId: this.formResponseId,
              scanId: this.orderId
            });

            // DESPUÉS subir las imágenes si las hay (en background)
            if (hasRequiredImages) {
              // Subir imágenes en background sin bloquear el flujo
              this.uploadRequiredImagesInBackground();
            }
          } else {
            if (requireScans) {
              // 3D scan is required
              this._router.navigate([`/self-scanning-instructions/${this.orderId}`], {
                queryParams: { scanId: this.orderId, customFormId: this.formId, formResponseId: this.formResponseId, clientId: this.clientId, referenceId: this.referenceId }
              });
            } else if (requireWeb3DScans) {
              // Buscar si hay alguna pregunta uniqueChoice con prescriptionTemplateId
              this.prescriptionTemplateId = this.getPrescriptionTemplateIdFromResponses(questions);
              const patientData = this.getPatientDataFromQuestions(questions);
              this.name = patientData.name;
              this.surname = patientData.surname;
              this.gender = patientData.gender;

              if (this.prescriptionTemplateId) {
                this._router.navigate([`/web-3d-scan/${this.prescriptionTemplateId}`], {
                  queryParams: {
                    orderId: this.orderId,
                    name: this.name,
                    surname: this.surname,
                    gender: this.gender,
                    clientId: this.clientId,
                    formResponseId: this.formResponseId,
                  }
                });
              } else {
                // Navigate to volumental without prescriptionTemplateId
                this._router.navigate(['/volumental'], {
                  queryParams: {
                    orderId: this.orderId,
                    name: this.name,
                    surname: this.surname,
                    gender: this.gender,
                    clientId: this.clientId,
                    formResponseId: this.formResponseId,
                  }
                });
              }
            } else {
              // 3D scan is not required
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Form has been submitted successfully' });
              this._router.navigate(['/home'])

            }
          }

          // Reset form
          this.myForm.reset();

        } catch (error) {
          console.error('❌ Error in form submission flow:', error);
          Swal.close();
          this.isSubmitting = false;

          // En caso de error, continuar con el flujo normal
          if (this.customFormFromInput) {
            this.customFormSubmitted.emit({
              formResponseId: this.formResponseId,
              scanId: this.orderId
            });
          } else {
            const requireScans = this.formResponse.requireScans;
            const requireWeb3DScans = this.formResponse.requireWeb3DScans

            if (requireScans) {
              this._router.navigate([`/self-scanning-instructions/${this.orderId}`], {
                queryParams: { scanId: this.orderId, customFormId: this.formId, formResponseId: this.formResponseId, clientId: this.clientId, referenceId: this.referenceId }
              });
            } else if (requireWeb3DScans) {
              // Buscar si hay alguna pregunta uniqueChoice con prescriptionTemplateId
              this.prescriptionTemplateId = this.getPrescriptionTemplateIdFromResponses(questions);
              const patientData = this.getPatientDataFromQuestions(questions);
              this.name = patientData.name;
              this.surname = patientData.surname;
              this.gender = patientData.gender;

              if (this.prescriptionTemplateId) {
                this._router.navigate([`/web-3d-scan/${this.prescriptionTemplateId}`], {
                  queryParams: {
                    orderId: this.orderId,
                    name: this.name,
                    surname: this.surname,
                    gender: this.gender,
                    clientId: this.clientId,
                    formResponseId: this.formResponseId,
                  }
                });
              } else {
                // Navigate to volumental without prescriptionTemplateId
                this._router.navigate(['/volumental'], {
                  queryParams: {
                    orderId: this.orderId,
                    name: this.name,
                    surname: this.surname,
                    gender: this.gender,
                    clientId: this.clientId,
                    formResponseId: this.formResponseId,
                  }
                });
              }
            } else {
              this._router.navigate(['/home'])
            }
          }
        }

      }, (err) => {
        this.isSubmitting = false;
        Swal.close();
        if (err.status != 500) {
          Swal.fire('Error', err.error.message, "error");
        } else {
          Swal.fire('Error', "Internal error", "error");
        }
      })

    } catch (error) {
      this.isSubmitting = false;
      Swal.close();
      console.error('Error uploading required images or submitting form:', error);
      Swal.fire('Error', 'Failed to process form. Please try again.', "error");
    }
  }

  isAnyResponseBlank(data: any[]): boolean {
    return data.some(item => Object.values(item).some(response => response == null || (typeof response === 'string' && response.trim() === '')));
  }


  getStringFromArray(arr: any) {
    let str = '';
    arr.forEach((item: any, index: any) => {
      str += item;
      if (index < arr.length - 1) {
        str += ', ';
      }
    })
    return str;
  }

  nextForm(step: number): void {
    const customFormControls = this.customFormControls.controls;
    const currentFormControl = customFormControls[step];

    if (currentFormControl.invalid || this.isFormBlank(currentFormControl)) {
      this.errorMessage = 'Please fill in the required fields.';
      return;
    }

    this.errorMessage = ''; // Limpiamos el mensaje si todo está bien

    const nextStep = this.getNextStepWithLogic(step);

    this.navigationHistory.push(nextStep);

    this.currentStep = nextStep;
  }

  getNextStepWithLogic(currentStep: number): number {
    const currentQuestion = this.fomrJson[currentStep];

    // Verificar si la pregunta es de tipo uniqueChoice y tiene lógica definida
    if (currentQuestion.type === 'uniqueChoice' && currentQuestion.logic && Array.isArray(currentQuestion.logic) && currentQuestion.logic.length > 0) {
      // Obtener la opción seleccionada
      const selectedChoice = currentQuestion.choices?.find((choice: any) => choice.isSelected === true);

      if (selectedChoice && selectedChoice.options && selectedChoice.options.length > 0) {
        const selectedOptionName = selectedChoice.options[0].name;

        // Buscar una regla que coincida con la opción seleccionada
        const matchingRule = currentQuestion.logic.find((rule: any) =>
          rule.selectedOption === selectedOptionName && rule.condition === 'If'
        );

        if (matchingRule && matchingRule.goToIndex !== null && matchingRule.goToIndex !== undefined) {
          // Retornar el índice especificado en la regla
          return matchingRule.goToIndex;
        }
      }
    }

    // Si no hay lógica o no coincide, ir al siguiente paso normal
    return currentStep + 1;
  }


  isFormBlank(control: AbstractControl): boolean {
    if (control instanceof FormGroup) {
      // Check if any control value in the FormGroup is blank, excluding 'productsList'
      return Object.keys(control.getRawValue()).some(key => key !== 'productsList' && (control.get(key)?.value === '' || control.get(key)?.value === null));
    } else {
      // Check if the single control value is blank
      return control.value === '' || control.value === null;
    }
  }

  backForm(step: number): void {
    // Remover el paso actual del historial
    if (this.navigationHistory.length > 1) {
      this.navigationHistory.pop();
      // Ir al paso anterior en el historial
      this.currentStep = this.navigationHistory[this.navigationHistory.length - 1];
    } else {
      // Si no hay historial, ir al paso anterior normal
      this.currentStep = step - 1;
    }
  }

  backToCustomForm() {
    this._router.navigate(['/custom-form']);
  }

  /**
   * Extrae el prescriptionTemplateId de las respuestas de uniqueChoice
   * Busca en todas las preguntas y retorna el primer prescriptionTemplateId encontrado
   * 
   * @param questions - Array de preguntas con las respuestas del usuario
   * @returns prescriptionTemplateId o null si no se encuentra
   */
  private getPrescriptionTemplateIdFromResponses(questions: any[]): string | null {
    console.log("Questions: ", questions)
    // Buscar preguntas de tipo uniqueChoice que tengan respuesta
    for (const question of questions) {
      if (question.type === 'uniqueChoice' && question.response && question.prescriptionTemplateId) {
        console.log('Found prescriptionTemplateId:', question.prescriptionTemplateId, 'for response:', question.response);
        return question.prescriptionTemplateId;
      }
    }

    console.log('No prescriptionTemplateId found in responses');
    return null;
  }

  onSelect(i: number, opIndex: number, control: any): void {
    // Toggle todas las opciones de esa card
    const choice = this.fomrJson[i].choices[opIndex];
    const allSelected = choice.options.every((opt: any) => opt.isSelected);

    choice.options.forEach((opt: any) => {
      opt.isSelected = !allSelected; // si todas estaban seleccionadas → deselecciona todo, si no → selecciona todo
    });

    this.setValueInFormControl(i, control);
  }

  btnOnSelect(i: number, opIndex: number, ind: number, control: any) {
    // Toggle solo la opción concreta
    const option = this.fomrJson[i].choices[opIndex].options[ind];
    option.isSelected = !option.isSelected;

    this.setValueInFormControl(i, control);
  }

  setValueInFormControl(i: any, formControlName: any) {
    let selectedArry: any = []
    let legSideArry: any = []

    this.fomrJson[i].choices.forEach((element: any) => {
      element.options.forEach((ele: any) => {
        if (ele.isSelected) {
          legSideArry.push(ele.name)
        }
      });
      if (legSideArry.length > 0) {
        selectedArry.push({
          title: element.title,
          image: element.image,
          legSide: legSideArry
        });
        legSideArry = []
      }
    });
    this.customFormArrayControls(i)?.controls[formControlName].setValue(selectedArry);
  }

  onSingleSelect(i: any, opIndex: any, control: any, ind?: any) {
    const formControlName = control;
    const clickedElement = this.fomrJson[i].choices[opIndex];

    if (ind !== undefined) {
      // Click event originated from a button
      this.fomrJson[i].choices[opIndex].isSelected = !this.fomrJson[i].choices[opIndex].isSelected;
    } else {
      // Click event originated from the div
      this.fomrJson[i].choices.forEach((element: any, index: any) => {
        if (index === opIndex) {
          element.isSelected = true;
          const singleSelectedObj = {
            image: element.image,
            title: element.title
          };

          this.customFormArrayControls(i).controls[formControlName].setValue(singleSelectedObj);
        } else {
          element.isSelected = false;
        }
      });
    }
  }


  checkboxChange(event: any, formArrayName: any, index: any, i: any) {
    let selectedCheck = this.customFormArrayControls(i).value[formArrayName] === '' || this.customFormArrayControls(i).value[formArrayName] === null ? [] : this.customFormArrayControls(i).value[formArrayName]
    if (selectedCheck[index]) {
      selectedCheck[index] = event.target.value;
    }
    this.customFormArrayControls(i).controls[formArrayName].setValue(selectedCheck);
  }

  increment(index: number) {
    this.productsList[index].quantity++;
  }

  decrement(index: number) {
    this.productsList[index].quantity--;
  }

  async checkout() {
    console.log("Initiating checkout with products:", this.productsList);
    this.isCheckoutModal = true;
    this.checkoutPayload = {};
    this.totalAmount = 0;
    const prices: any = [];
    const stripeAccount = this.formResponse.connectedStripeId
    this.productsList.forEach((product, index) => {
      prices.push({ price: product.price.id, quantity: product.quantity });
      this.totalAmount += product.price.unit_amount * product.quantity;
    })
    this.checkoutPayload.prices = prices;
    this.checkoutPayload.stripeAccount = stripeAccount;
    this.formResponseId = uuidv4();
    this.checkoutPayload.formResponseId = this.formResponseId;
    this.checkoutPayload.requireScans = this.formResponse.requireScans ? true : false;
    this.checkoutPayload.scanId = this.orderId;
    this.checkoutPayload.customFormId = this.formResponse.ID
    this.checkoutPayload.clientId = this.formResponse.clientId


    if (this.checkoutPayload.requireScans) {
      this.orderId = uuidv4();
      this.checkoutPayload.finalDestination = "selfScanningInstructions"
    }

    if (this.formResponse.requireWeb3DScans) {
      console.log("Web3DScans required, setting final destination to web3Dscans");
      this.checkoutPayload.finalDestination = "web3Dscans"

      try {
        const data = this.customFormControls.getRawValue();


        const responseData: any = {}
        const questionData: any = JSON.parse(JSON.stringify(this.formResponse.questions));

        // this.isTableData = true
        data.forEach(item => {
          const keys = Object.keys(item);
          keys.forEach(key => {
            if (!responseData[key]) {
              responseData[key] = [];
            }
            responseData[key].push(item[key]);
          });
        });

        this.formResponseId = this.formResponseId ?? uuidv4()
        this.orderId = this.orderId ?? uuidv4()

        this.payload = {
          ID: this.formResponseId,
          formId: this.formResponse.ID,
          formTitle: this.formResponse.name,
          requireScans: this.formResponse.requireScans ? true : false,
          requireWeb3DScans: this.formResponse.requireWeb3DScans ? true : false,
          questions: null,
          orderId: this.orderId
        }
        const questions: any = [];
        for (let index = 0; index < data.length; index++) {
          const response = data[index];
          const res = response[Object.keys(response)[0]];

          if (questionData[index].type === 'multipleChoice') {
            const tempRes: any = [];
            res.forEach((r: any) => {
              tempRes.push({
                title: r.title || '',
                image: r.image,
                responses: this.getStringFromArray(r.legSide)
              })
            })
            questionData[index].responses = tempRes;
            questionData[index].image = res[0].image;
            delete questionData[index].choices;
            delete questionData[index].required;
            delete questionData[index].subtitle;
            questions.push(questionData[index]);
          } else if (questionData[index].type === 'uniqueChoice') {
            questionData[index].image = res.image;
            const response = questionData[index].choices.filter((choice: any) => choice.isSelected === true);
            questionData[index].response = response[0].options[0].name;

            // Guardar el prescriptionTemplateId si existe en la choice seleccionada
            if (response[0]?.prescriptionTemplateId) {
              questionData[index].prescriptionTemplateId = response[0].prescriptionTemplateId;
              console.log('Saving prescriptionTemplateId:', response[0].prescriptionTemplateId, 'for question:', questionData[index].title);

              try {
                const prescriptionTemplate = await lastValueFrom(
                  this.prescriptionTemplatesService.getPrescriptionTemplateByID(response[0].prescriptionTemplateId)
                );

                if (prescriptionTemplate?.prescription?.components) {
                  const question = {
                    title: "Prescription",
                    type: "insolesPrescription",
                    components: prescriptionTemplate.prescription.components
                  };
                  questions.push(question);
                }
              } catch (error) {
                console.error('Error loading prescription template:', error);
              }
            }

            delete questionData[index].choices;
            delete questionData[index].required;
            delete questionData[index].subtitle;
            questions.push(questionData[index]);
          } else if (questionData[index].type === 'customComponents') {

            res.forEach((r: any, i: any) => {
              r.numberInputWithLabels?.forEach((v: any, j: any) => {
                questionData[index].components[i].choices.numberInputWithLabels[j].inputNumberValue = v.inputNumberValue || v;
              })
              r.dropdownWithLabel?.forEach((v: any, j: any) => {
                questionData[index].components[i].choices.dropdownWithLabel[j].response = v.response || v;
                delete questionData[index].components[i].choices.dropdownWithLabel[j].type;
                delete questionData[index].components[i].choices.dropdownWithLabel[j].options;
              })
              r.radioButtons?.forEach((v: any, j: any) => {
                questionData[index].components[i].choices.radioButtons[j].response = v.response || v;
                delete questionData[index].components[i].choices.radioButtons[j].type;
                delete questionData[index].components[i].choices.radioButtons[j].options;
              })

              r.checkboxWithLabel?.forEach((v: any, j: any) => {
                const responses: any = [];
                v.forEach((check: any, k: any) => {
                  if (check === true) {
                    responses.push(questionData[index].components[i].choices.checkboxWithLabel[j].options[k]);
                  }
                })
                questionData[index].components[i].choices.checkboxWithLabel[j].responses = responses;
                delete questionData[index].components[i].choices.checkboxWithLabel[j].type;
                delete questionData[index].components[i].choices.checkboxWithLabel[j].default;
                delete questionData[index].components[i].choices.checkboxWithLabel[j].options;
              })
              questionData[index].components[i].choices.multipleCheckboxWithLabel = questionData[index].components[i].choices.checkboxWithLabel;
              questionData[index].components[i].choices.checkboxWithLabel = [];
            })
            questions.push(questionData[index])
            delete questionData[index].required;
            delete questionData[index].subtitle;
          } else if (questionData[index].type === 'insolesPrescription') {

            const customFormArray = this.customFormControls;
            const foundInsolePrescription = customFormArray.controls.find((component) => component.get('type') && component.get('type')!.value)

            const componentsArray = foundInsolePrescription!.get('components') as FormArray;

            // Filter only the componentes that the form is valid and has some foot selected
            // Because when nothing it's selected as "foot" it's always valid
            const validFormsWithFootSelection = componentsArray.controls.filter((formGroup: any) => {
              return formGroup.valid && formGroup.get('footSelection')!.value;
            });

            // Prepare array of components to request
            let componentsToRequest = [];

            // Add all the valid components to the components list
            validFormsWithFootSelection.forEach(formGroup => {
              componentsToRequest.push(formGroup.value)
            });

            // Map the data to request, and list only components "valid" and with Foot selected
            const insolePrescriptionForm = {
              ...foundInsolePrescription!.value,
              title: questionData[index].title,
              components: validFormsWithFootSelection.map(component => component.value)
            };

            // Agregar prescriptionTemplateId si fue guardado desde la template
            if (foundInsolePrescription!.get('prescriptionTemplateId')?.value) {
              insolePrescriptionForm.prescriptionTemplateId = foundInsolePrescription!.get('prescriptionTemplateId')!.value;
            }

            questions.push(insolePrescriptionForm);
          } else if (questionData[index].type === 'requiredImages') {
            const question: any = {};
            question.title = questionData[index].title;
            question.type = questionData[index].type;
            // Copy the ID if it exists (important for patient data questions)
            if (questionData[index].id) {
              question.id = questionData[index].id;
            }

            // Inicialmente crear la pregunta sin el ZIP URL
            question.responses = res.map((response: any) => ({
              title: response.title,
              exampleImage: response.exampleImage,
              index: response.index
            }));

            questions.push(question);
          } else if (questionData[index].type === 'dropdown') {
            const question: any = {};
            question.title = questionData[index].title;
            question.type = questionData[index].type;
            // Para dropdown, res contiene directamente el valor seleccionado
            question.response = res || '';
            // Copy the ID if it exists (important for patient data questions)
            if (questionData[index].id) {
              question.id = questionData[index].id;
            }
            questions.push(question);
          } else {
            const question: any = {};
            question.title = questionData[index].title;
            question.type = questionData[index].type;
            question.response = res || '';
            // Copy the ID if it exists (important for patient data questions)
            if (questionData[index].id) {
              question.id = questionData[index].id;
            }
            questions.push(question);
          }

        }
        this.payload.questions = questions;

        try {
          Swal.close()
          this.isSubmitting = false;

          // Buscar si hay alguna pregunta uniqueChoice con prescriptionTemplateId
          this.prescriptionTemplateId = this.getPrescriptionTemplateIdFromResponses(questions);
          const patientData = this.getPatientDataFromQuestions(questions);
          this.name = patientData.name;
          this.surname = patientData.surname;
          this.gender = patientData.gender;

          this.checkoutPayload.prescriptionTemplateId = this.prescriptionTemplateId ? this.prescriptionTemplateId : null
          this.checkoutPayload.patientInfo = {
            name: this.name,
            surname: this.surname,
            gender: this.gender
          }

        } catch (error) {
          console.error('❌ Error in form submission flow:', error);
          Swal.close();
          this.isSubmitting = false;
        }
      } catch (error) {
        this.isSubmitting = false;
        Swal.close();
        console.error('Error during checkout:', error);
        Swal.fire('Error', 'Failed to process form. Please try again.', "error");
      }

    }
  }

  hasSelectedProducts(): boolean {
    return this.productsList && this.productsList.some(product => product.quantity > 0);
  }

  getProductImage(item: any): string {
    return item.product.images[0];
  }

  buyNow() {
    this.loading = true;

    const email = this.emailForm.value.email

    this.checkoutPayload.email = email

    this.stripeService.getPaymentLink(this.checkoutPayload).subscribe({
      next: async (res: any) => {
        this.isCheckoutModal = false;
        this.loading = false;

        if (res) {
          const data = this.customFormControls.getRawValue();

          const responseData: any = {}
          const questionData: any = JSON.parse(JSON.stringify(this.formResponse.questions));

          data.forEach(item => {
            const keys = Object.keys(item);
            keys.forEach(key => {
              if (!responseData[key]) {
                responseData[key] = [];
              }
              responseData[key].push(item[key]);
            });
          });

          this.payload = {
            ID: this.formResponseId,
            formId: this.formResponse.ID,
            formTitle: this.formResponse.name,
            requireScans: this.checkoutPayload.requireScans,
            requireWeb3DScans: this.checkoutPayload.requireWeb3DScans,
            questions: null,
            orderId: this.orderId,
            email: email
          }

          const questions: any = [];
          for (let index = 0; index < data.length; index++) {
            const response = data[index];
            const res = response[Object.keys(response)[0]];


            if (questionData[index].type === 'multipleChoice') {
              const tempRes: any = [];
              res.forEach((r: any) => {
                tempRes.push({
                  title: r.title || '',
                  image: r.image,
                  responses: this.getStringFromArray(r.legSide)
                })
              })
              questionData[index].responses = tempRes;
              questionData[index].image = res[0].image;
              delete questionData[index].choices;
              delete questionData[index].required;
              delete questionData[index].subtitle;
              questions.push(questionData[index]);
            } else if (questionData[index].type === 'uniqueChoice') {
              questionData[index].image = res.image;
              const response = questionData[index].choices.filter((choice: any) => choice.isSelected === true);
              questionData[index].response = response[0].options[0].name;

              // Guardar el prescriptionTemplateId si existe en la choice seleccionada
              if (response[0]?.prescriptionTemplateId) {
                questionData[index].prescriptionTemplateId = response[0].prescriptionTemplateId;
                console.log('Saving prescriptionTemplateId:', response[0].prescriptionTemplateId, 'for question:', questionData[index].title);

                try {
                  const prescriptionTemplate = await lastValueFrom(
                    this.prescriptionTemplatesService.getPrescriptionTemplateByID(response[0].prescriptionTemplateId)
                  );

                  if (prescriptionTemplate?.prescription?.components) {
                    const question = {
                      title: "Prescription",
                      type: "insolesPrescription",
                      components: prescriptionTemplate.prescription.components
                    };
                    questions.push(question);
                  }
                } catch (error) {
                  console.error('Error loading prescription template:', error);
                }
              }

              delete questionData[index].choices;
              delete questionData[index].required;
              delete questionData[index].subtitle;
              questions.push(questionData[index]);
            } else if (questionData[index].type === 'customComponents') {

              res.forEach((r: any, i: any) => {
                r.numberInputWithLabels.forEach((v: any, j: any) => {
                  questionData[index].components[i].choices.numberInputWithLabels[j].inputNumberValue = v;
                })
                r.dropdownWithLabel.forEach((v: any, j: any) => {
                  questionData[index].components[i].choices.dropdownWithLabel[j].response = v;
                  delete questionData[index].components[i].choices.dropdownWithLabel[j].type;
                  delete questionData[index].components[i].choices.dropdownWithLabel[j].options;
                })
                r.radioButtons.forEach((v: any, j: any) => {
                  questionData[index].components[i].choices.radioButtons[j].response = v;
                  delete questionData[index].components[i].choices.radioButtons[j].type;
                  delete questionData[index].components[i].choices.radioButtons[j].options;
                })

                r.checkboxWithLabel.forEach((v: any, j: any) => {
                  const responses: any = [];
                  v.forEach((check: any, k: any) => {
                    if (check === true) {
                      responses.push(questionData[index].components[i].choices.checkboxWithLabel[j].options[k]);
                    }
                  })

                  questionData[index].components[i].choices.checkboxWithLabel[j].responses = responses;
                  delete questionData[index].components[i].choices.checkboxWithLabel[j].type;
                  delete questionData[index].components[i].choices.checkboxWithLabel[j].default;
                  delete questionData[index].components[i].choices.checkboxWithLabel[j].options;
                })
                questionData[index].components[i].choices.multipleCheckboxWithLabel = questionData[index].components[i].choices.checkboxWithLabel;
                questionData[index].components[i].choices.checkboxWithLabel = [];
              })
              questions.push(questionData[index])
              delete questionData[index].required;
              delete questionData[index].subtitle;
            }

            else if (questionData[index].type === 'insolesPrescription') {
              const customFormArray = this.customFormControls;
              const foundInsolePrescription = customFormArray.controls.find((component) => component.get('type') && component.get('type')!.value)

              const componentsArray = foundInsolePrescription!.get('components') as FormArray;

              // Filter only the componentes that the form is valid and has some foot selected
              const validFormsWithFootSelection = componentsArray.controls.filter((formGroup: any) => {
                return formGroup.valid && formGroup.get('footSelection')!.value;
              });

              // Map the data to request, and list only components "valid" and with Foot selected
              const insolePrescriptionForm = {
                ...foundInsolePrescription!.value,
                title: questionData[index].title,
                components: validFormsWithFootSelection.map(component => component.value)
              };

              // Agregar prescriptionTemplateId si fue guardado desde la template
              if (foundInsolePrescription!.get('prescriptionTemplateId')?.value) {
                insolePrescriptionForm.prescriptionTemplateId = foundInsolePrescription!.get('prescriptionTemplateId')!.value;
              }

              questions.push(insolePrescriptionForm);
            }

            else if (questionData[index].type === 'productsList') {
              const productsListResponse = {
                title: questionData[index].title,
                type: questionData[index].type,
                response: this.productsList[0]['product']['images'][0]
              };
              questions.push(productsListResponse);
            }


            else {
              const question: any = {};
              question.title = questionData[index].title;
              question.type = questionData[index].type;
              question.response = res;
              // Copy the ID if it exists (important for patient data questions)
              if (questionData[index].id) {
                question.id = questionData[index].id;
              }
              questions.push(question);
            }

          }
          this.payload.questions = questions;

          this._formResponseService.submitFormResponse(this.payload).subscribe((res) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Form has been submitted successfully' });
          })
        }

        const baseUrl = res.paymentLinkCreated.url
        const finalUrl = `${baseUrl}?prefilled_email=${encodeURIComponent(email)}`

        window.location.href = finalUrl
        this._router.navigate([`/paid-form/formResponse/${this.formResponseId}/paid/${this.checkoutPayload.email}`]);

        // Reset form
        this.myForm.reset();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Something went wrong!' });
        this.isCheckoutModal = false;
        this.loading = false;
      }
    })
  }

  isProductListInForm() {
    // Return true if the form has productList in any index else false
    let y = false;
    this.formResponse.questions.forEach((x: any) => {
      if (x.type == 'productsList') y = true;
    })
    return y;
  }

  static noSpaceValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value && value.toString().trim().length === 0) {
        return { noWhiteSpaceValidator: true };
      }
      return null;
    };
  }

  getUserUploadedImage(questionIndex: number, imageIndex: number): string | null {
    const key = `${questionIndex}_${imageIndex}`;
    const imageData = this.userUploadedImages[key];
    return imageData ? imageData.preview : null;
  }

  triggerFileInput(questionIndex: number, imageIndex: number): void {
    // Crear un ID único para el input file específico
    const inputId = `fileInput_${questionIndex}_${imageIndex}`;
    const targetInput = document.getElementById(inputId) as HTMLInputElement;
    if (targetInput) {
      targetInput.click();
    }
  }

  onImageUpload(event: any, questionIndex: number, imageIndex: number): void {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

      if (!allowedTypes.includes(file.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please select a valid image file (JPEG, PNG, GIF, WebP) or PDF'
        });
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'File size must be less than 10MB'
        });
        return;
      }

      const key = `${questionIndex}_${imageIndex}`;

      // Si es PDF, no necesitamos preview
      if (file.type === 'application/pdf') {
        this.userUploadedImages[key] = {
          preview: 'pdf',
          file: file,
          uploaded: false,
          s3Url: null
        };

        // Actualizar el FormControl correspondiente
        this.updateRequiredImagesFormControl(questionIndex);

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'PDF selected successfully'
        });
      } else {
        // Para imágenes, crear preview
        const reader = new FileReader();
        reader.onload = (e: any) => {
          // Almacenar tanto la imagen base64 para preview como el archivo original
          this.userUploadedImages[key] = {
            preview: e.target.result,
            file: file,
            uploaded: false,
            s3Url: null
          };

          // Actualizar el FormControl correspondiente
          this.updateRequiredImagesFormControl(questionIndex);

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Image selected successfully'
          });
        };

        reader.readAsDataURL(file);
      }
    }

    // Limpiar el input para permitir subir el mismo archivo nuevamente
    event.target.value = '';
  }

  isUploadedFilePDF(questionIndex: number, imageIndex: number): boolean {
    const key = `${questionIndex}_${imageIndex}`;
    const imageData = this.userUploadedImages[key];
    return imageData ? imageData.file.type === 'application/pdf' : false;
  }

  getUploadedFileName(questionIndex: number, imageIndex: number): string {
    const key = `${questionIndex}_${imageIndex}`;
    const imageData = this.userUploadedImages[key];
    return imageData ? imageData.file.name : '';
  }

  private updateRequiredImagesFormControl(questionIndex: number): void {
    const question = this.fomrJson[questionIndex];
    if (question.type === 'requiredImages') {
      const uploadedImages: any[] = [];

      question.choices?.forEach((choice: any, index: number) => {
        const key = `${questionIndex}_${index}`;
        const imageData = this.userUploadedImages[key];

        if (imageData) {
          uploadedImages.push({
            title: choice.title,
            exampleImage: choice.image,
            userImage: imageData.s3Url || imageData.preview, // Usar S3 URL si está disponible, sino preview
            index: index,
            file: imageData.file,
            uploaded: imageData.uploaded
          });
        }
      });

      // Actualizar el FormControl con las imágenes subidas
      const formControl = this.customFormControls.controls[questionIndex];
      if (formControl instanceof FormGroup) {
        const requiredImagesControl = formControl.get('requiredImages');
        if (requiredImagesControl) {
          requiredImagesControl.setValue(uploadedImages);
        } else {
          // Si no existe el control, crearlo
          formControl.addControl('requiredImages', this._fb.control(uploadedImages));
        }
      }
    }
  }

  // Validador personalizado para requiredImages
  private requiredImagesValidator(expectedCount: number): ValidatorFn {
    return (control: AbstractControl) => {
      const uploadedImages = control.value || [];
      if (uploadedImages.length < expectedCount) {
        return { requiredImages: { expected: expectedCount, actual: uploadedImages.length } };
      }
      return null;
    };
  }

  // Método para crear ZIP con las imágenes de requiredImages (basado en files.component.ts)
  private createRequiredImagesZip(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      const zip = new JSZip();

      // Función para generar nombres de archivo únicos
      const generateFileName = (questionIndex: number, imageIndex: number, originalFile: File): string => {
        const question = this.fomrJson[questionIndex];
        const choice = question.choices[imageIndex];
        const fileExtension = originalFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const sanitizedTitle = choice.title.replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `${sanitizedTitle}.${fileExtension}`;
        return fileName;
      };

      // Agregar todas las imágenes subidas al ZIP
      const imagePromises: Promise<void>[] = [];

      Object.entries(this.userUploadedImages).forEach(([key, imageData]) => {

        if (imageData.file) {
          const [questionIndex, imageIndex] = key.split('_').map(Number);

          try {
            const fileName = generateFileName(questionIndex, imageIndex, imageData.file);

            const promise = new Promise<void>((resolveFile) => {
              zip.file(fileName, imageData.file);
              resolveFile();
            });

            imagePromises.push(promise);
          } catch (error) {
            console.error('❌ Error processing file for key:', key, error);
          }
        } else {
          console.warn('⚠️ No file found for key:', key);
        }
      });

      if (imagePromises.length === 0) {
        console.warn('⚠️ No images to add to ZIP');
        reject(new Error('No images to add to ZIP'));
        return;
      }

      // Esperar a que todas las imágenes se agreguen al ZIP
      Promise.all(imagePromises).then(() => {
        // Generar el archivo ZIP
        zip.generateAsync({ type: 'blob' })
          .then((blob) => {
            console.log('✅ ZIP blob generated successfully, size:', blob.size, 'bytes');
            resolve(blob);
          })
          .catch((err) => {
            console.error('❌ Error generating ZIP blob:', err);
            reject(err);
          });
      }).catch((err) => {
        console.error('❌ Error adding images to ZIP:', err);
        reject(err);
      });
    });
  }

  private async uploadRequiredImagesZipToS3(): Promise<string> {
    try {
      // Agregar un pequeño delay para asegurar que el order esté disponible en el backend
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 segundos de delay

      // Obtener URL firmada para subir el ZIP con retry logic
      let signedZipUrlResponse;
      let retries = 3;

      while (retries > 0) {
        try {
          signedZipUrlResponse = await lastValueFrom(this.scanService.getSignedZipUrl(this.orderId));
          break; // Si tiene éxito, salir del loop
        } catch (error) {
          retries--;
          console.warn(`⚠️ Failed to get signed ZIP URL, retries left: ${retries}`, error);

          if (retries === 0) {
            throw error; // Si no quedan reintentos, lanzar el error
          }

          // Esperar antes del siguiente intento
          await new Promise(resolve => setTimeout(resolve, 5000)); // 5 segundos entre reintentos
        }
      }

      const signedZipUrl = signedZipUrlResponse.signedZipUrl;

      if (!signedZipUrl) {
        console.error('❌ No signed URL available for upload');
        throw new Error('No signed URL available for upload');
      }

      // Crear el ZIP con las imágenes
      const zipBlob = await this.createRequiredImagesZip();

      // Subir el ZIP a S3
      await lastValueFrom(this.scanService.uploadZipFile(signedZipUrl, zipBlob));

      const cleanUrl = signedZipUrl.split('?')[0]; // Remover query parameters para obtener la URL limpia

      // Asignar userImage a cada choice usando la cleanUrl
      await this.assignUserImageToChoices(cleanUrl);

      return cleanUrl;

    } catch (error) {
      console.error('❌ Error uploading required images ZIP:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        orderId: this.orderId,
        userUploadedImages: Object.keys(this.userUploadedImages)
      });
      throw error;
    }
  }

  // Método para sincronizar archivos S3 con el scan
  private async syncS3FilesWithScan(orderId: string): Promise<void> {
    try {
      const syncResponse = await lastValueFrom(this.scanService.syncS3fileToScan(orderId));
      console.log('✅ S3 sync completed successfully:', syncResponse);
    } catch (error) {
      console.error('❌ Error syncing S3 files with scan:', error);
      console.error('❌ Sync error details:', {
        message: error.message,
        stack: error.stack,
        orderId: orderId
      });
      throw error;
    }
  }

  // Método para asignar userImage a cada choice usando la cleanUrl
  private async assignUserImageToChoices(cleanUrl: string): Promise<void> {
    try {
      // Obtener la URL base sin el nombre del archivo ZIP
      const urlParts = cleanUrl.split('/');
      const baseUrl = urlParts.slice(0, -1).join('/'); // Remover el nombre del archivo ZIP

      let hasUpdatedImages = false;

      this.fomrJson.forEach((question, questionIndex) => {
        if (question.type === 'requiredImages' && question.choices) {
          question.choices.forEach((choice: any, choiceIndex: number) => {
            const key = `${questionIndex}_${choiceIndex}`;
            const imageData = this.userUploadedImages[key];

            if (imageData && imageData.file) {
              // Generar el nombre del archivo usando la misma lógica que en createRequiredImagesZip
              const fileExtension = imageData.file.name.split('.').pop()?.toLowerCase() || 'jpg';
              const sanitizedTitle = choice.title.replace(/[^a-zA-Z0-9]/g, '_');
              const fileName = `${sanitizedTitle}.${fileExtension}`;

              // Crear la URL completa para esta imagen específica
              const userImageUrl = `${baseUrl}/${fileName}`;

              // Asignar la userImage al choice
              choice.userImage = userImageUrl;
              hasUpdatedImages = true;
            } else {
              console.warn(`⚠️ No image data found for choice "${choice.title}" at key:`, key);
            }
          });
        }
      });

      // Actualizar el form response en la base de datos si se asignaron imágenes
      if (hasUpdatedImages && this.formResponseId) {
        await this.updateFormResponseWithUserImages();
      }

    } catch (error) {
      console.error('❌ Error assigning userImage to choices:', error);
    }
  }

  private async updateFormResponseWithUserImages(): Promise<void> {
    try {
      // Usar las respuestas guardadas antes del reset
      const formData = this.savedFormData.length > 0 ? this.savedFormData : this.customFormControls.getRawValue();

      // Transformar las preguntas para el formato que espera el backend
      const transformedQuestions = this.fomrJson.map((question, index) => {
        const formResponse = formData[index];

        if (question.type === 'requiredImages' && question.choices) {
          // Transformar choices a responses para requiredImages
          const responses = question.choices.map((choice: any) => ({
            title: choice.title || '',
            exampleImage: choice.image || '',
            userImage: choice.userImage || '',
          }));

          return {
            title: question.title || '',
            type: question.type,
            responses: responses
          };
        } else if (question.type === 'dropdown') {
          // Para dropdown, el valor está en formResponse.dropdown
          const dropdownValue = formResponse?.dropdown || formResponse?.[question.type] || '';

          return {
            title: question.title || '',
            type: question.type,
            response: dropdownValue
          };
        } else if (question.type === 'inputText' || question.type === 'longText' || question.type === 'email') {
          // Para campos de texto, el valor está en formResponse[question.type]
          const textValue = formResponse?.[question.type] || '';

          return {
            title: question.title || '',
            type: question.type,
            response: textValue
          };
        } else if (question.type === 'uniqueChoice') {
          // Para uniqueChoice, incluir la respuesta
          const selectedChoice = question.choices?.find((choice: any) => choice.isSelected === true);
          const uniqueChoiceValue = formResponse?.uniqueChoice || formResponse?.[question.type];

          return {
            title: question.title || '',
            type: question.type,
            image: uniqueChoiceValue?.image || '',
            response: selectedChoice?.options?.[0]?.name || ''
          };
        } else if (question.type === 'multipleChoice') {
          // Para multipleChoice, incluir las respuestas
          const responses: any[] = [];
          question.choices?.forEach((choice: any) => {
            const selectedOptions = choice.options?.filter((opt: any) => opt.isSelected);
            if (selectedOptions && selectedOptions.length > 0) {
              responses.push({
                title: choice.title || '',
                image: choice.image || '',
                responses: selectedOptions.map((opt: any) => opt.name).join(', ')
              });
            }
          });
          return {
            title: question.title || '',
            type: question.type,
            responses: responses
          };
        } else if (question.type === 'customComponents') {
          // Para customComponents, procesar los componentes y sus opciones
          const res = formResponse?.components || [];
          res.forEach((r: any, i: any) => {
            r.numberInputWithLabels.forEach((v: any, j: any) => {
              question.components[i].choices.numberInputWithLabels[j].inputNumberValue = v;
            });
            r.dropdownWithLabel.forEach((v: any, j: any) => {
              question.components[i].choices.dropdownWithLabel[j].response = v;
              delete question.components[i].choices.dropdownWithLabel[j].type;
              delete question.components[i].choices.dropdownWithLabel[j].options;
            });
            r.radioButtons.forEach((v: any, j: any) => {
              question.components[i].choices.radioButtons[j].response = v;
              delete question.components[i].choices.radioButtons[j].type;
              delete question.components[i].choices.radioButtons[j].options;
            });
            r.checkboxWithLabel.forEach((v: any, j: any) => {
              const responses: any = [];
              v.forEach((check: any, k: any) => {
                if (check === true) {
                  responses.push(question.components[i].choices.checkboxWithLabel[j].options[k]);
                }
              });
              question.components[i].choices.checkboxWithLabel[j].responses = responses;
              delete question.components[i].choices.checkboxWithLabel[j].type;
              delete question.components[i].choices.checkboxWithLabel[j].default;
              delete question.components[i].choices.checkboxWithLabel[j].options;
            });
            question.components[i].choices.multipleCheckboxWithLabel = question.components[i].choices.checkboxWithLabel;
            question.components[i].choices.checkboxWithLabel = [];
          });

          return {
            title: question.title || '',
            type: question.type,
            components: question.components
          };
        }

        // Para otros tipos de preguntas, intentar obtener el valor del tipo correspondiente
        const genericValue = formResponse?.[question.type] || formResponse?.[Object.keys(formResponse)[0]] || '';

        return {
          title: question.title || '',
          type: question.type,
          response: genericValue
        };
      });

      // Preparar el payload con las preguntas transformadas
      const updatePayload = {
        ID: this.formResponseId,
        formId: this.formResponse.ID,
        formTitle: this.formResponse.name,
        requireScans: this.formResponse.requireScans ? true : false,
        requireWeb3DScans: this.formResponse.requireWeb3DScans ? true : false,
        questions: transformedQuestions,
        orderId: this.orderId
      };

      await lastValueFrom(this._formResponseService.updateFormResponse(updatePayload));
    } catch (error) {
      console.error('❌ Error updating form response with userImages:', error);
      console.error('❌ Update error details:', {
        message: error.message,
        stack: error.stack,
        formResponseId: this.formResponseId,
        orderId: this.orderId
      });
      // No lanzar el error para no interrumpir el flujo principal
    }
  }

  // Método para subir imágenes en background (después de emitir el evento)
  private async uploadRequiredImagesInBackground(): Promise<void> {
    try {

      await this.uploadRequiredImagesZipToS3();
      await this.syncS3FilesWithScan(this.orderId);

    } catch (uploadError) {
      console.error('❌ Error uploading images in background:', uploadError);
      console.error('❌ Background upload error details:', {
        message: uploadError.message,
        stack: uploadError.stack,
        orderId: this.orderId,
        formResponseId: this.formResponseId,
        userUploadedImagesCount: Object.keys(this.userUploadedImages).length,
        userUploadedImagesKeys: Object.keys(this.userUploadedImages)
      });

      console.warn('⚠️ Background image upload failed, but order was created successfully');
    }
  }
}
